import React, { useEffect, useState, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { getAdmFileUrl } from '../../utils/admProgress';
import {
  Box, Typography, Button, Stack, Chip, CircularProgress, Alert, Snackbar,
  Card, CardContent, Paper, Divider, IconButton, Tooltip, useMediaQuery
} from '@mui/material';
import { Refresh, AccountBalance, Receipt, CheckCircle, PendingActions, Image as ImageIcon } from '@mui/icons-material';
import { getBuktiTransferMonitoring } from '../API/supplierPenyedia';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

const statusColor = (s) => s === 'Selesai' ? 'success' : s === 'Sedang Berlangsung' ? 'warning' : s === 'Menunggu Verifikasi' ? 'info' : 'default';

const SupplierBuktiTransfer = () => {
  const isMobile = useMediaQuery('(max-width:800px)');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await getBuktiTransferMonitoring();
      setList(res.data?.data || []);
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Gagal memuat data', severity: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              <AccountBalance sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Monitoring Bukti Transfer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bukti transfer dana (ADM) dari bantuan yang Anda layani.
            </Typography>
          </Box>
          <Tooltip title="Perbarui">
            <span>
              <IconButton onClick={() => fetchData(true)} disabled={refreshing || loading} size="small"
                sx={{ border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : list.length === 0 ? (
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Receipt sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>Belum ada bantuan yang dilayani</Typography>
              <Typography variant="body2" color="text.secondary">
                Data bukti transfer akan muncul setelah ada pembelian untuk bantuan Anda dan dokumen ADM Transfer Dana diupload.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {list.map((row) => (
              <Card key={row.BantuanId} variant="outlined">
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        #{row.BantuanId} — {row.namaPenerima}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.jenisBantuan} · {row.desaKelurahan}, {row.kecamatan}
                      </Typography>
                    </Box>
                    <Chip size="small" color={statusColor(row.status)} label={row.status}
                      icon={row.status === 'Selesai' ? <CheckCircle sx={{ fontSize: 14 }} /> : <PendingActions sx={{ fontSize: 14 }} />} />
                  </Stack>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Dokumen Bukti Transfer ({row.buktiTransfer.length})
                  </Typography>
                  {row.buktiTransfer.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Belum ada dokumen bukti transfer yang diupload.</Typography>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {row.buktiTransfer.map((file, i) => (
                        <Button key={i} size="small" variant="outlined" startIcon={<ImageIcon />}
                          sx={{ textTransform: 'none' }}
                          onClick={() => openInAppFilePreview({ url: getAdmFileUrl(file, null, BASE_URL), title: `Bukti Transfer ${i + 1}` })}>
                          Dokumen {i + 1}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default SupplierBuktiTransfer;
